import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import SelectField from '../fields/SelectField';
import NumberField from '../fields/NumberField';
import { hasPrivilege } from '../../hocs/withPrivileges';
import Privileges from '../../utils/Privileges';
import TextField from '../fields/TextField';
import BidsPlaceBidButton from './BidsPlaceBidButton';
import useMultiValidator from '../../hooks/useMultiValidator';
import { membersValidator } from '../../validators/members';
import { usersValidator } from '../../validators/users';
import { priceValidator } from '../../validators/price';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import { ALL_BIDS_QUERY_NAME, SUBMIT_SLOTTED_BIDS } from '../../graphql/bids';
import useAuthentication from '../../hooks/useAuthentication';
import { getUserIdFromToken } from '../../utils/authentication';
import { auctionMembersType, auctionType, bidsType } from '../../types';
import './_styles_/BidsPlaceBid.css';
import useExtendedState from '../../hooks/useExtendedState';
import useExtendedArrayState from '../../hooks/useExtendedArrayState';
import ReadonlyField from '../fields/ReadonlyField';
import {
    getProductType,
    SECURITY,
} from '../../utils/ProductTypes';
import { getBiddingUserConfiguration } from '../../reducers';
import WarningPanel from '../WarningPanel';
import useSingleValidator from '../../hooks/useSingleValidator';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

BidsPlaceBidSlotted.propTypes = {
    auction: auctionType.isRequired,
    auctionMembers: auctionMembersType.isRequired,
    isPriceStepValid: PropTypes.bool.isRequired,
    priceStep: PropTypes.number.isRequired,
    onBidAdded: PropTypes.func.isRequired,
    bids: bidsType.isRequired,
};

function BidsPlaceBidSlotted(props) { // NOSONAR javascript:S3776
    const {
        auction,
        auctionMembers,
        isPriceStepValid,
        priceStep,
        onBidAdded,
        bids,
    } = props;
    const { t } = useTranslation();
    const productType = getProductType(auction.product.productType);
    const { userPrivileges, accessToken } = useAuthentication();
    const currentUserRacf = getUserIdFromToken(accessToken);
    const hasMoreMembers = auctionMembers.length > 1;
    const initialQuantity = 0;
    const minQuantityState = useExtendedState(0);
    const initialMaxQuantity = 0;
    const maxQuantityState = useExtendedState(initialMaxQuantity);
    const biddingUserConfiguration = useSelector(getBiddingUserConfiguration);
    let isConflictWithBiddingUserConfigurationDefaultPrice = false;
    let initialPrice = auction.minPrice;
    if (biddingUserConfiguration.defaultPrice !== null) {
        if ((!auction.minPrice || biddingUserConfiguration.defaultPrice >= auction.minPrice)
            && (!auction.maxPrice || biddingUserConfiguration.defaultPrice <= auction.maxPrice)) {
            initialPrice = biddingUserConfiguration.defaultPrice;
        } else {
            isConflictWithBiddingUserConfigurationDefaultPrice = true;
        }
    }
    const prices = useExtendedArrayState(initialPrice);
    const asBrokerReferenceState = useExtendedState('');
    const selectedMemberIdState = useExtendedState(hasMoreMembers ? '' : auctionMembers[0].memberId);
    const [users, setUsers] = useState(hasMoreMembers ? [] : auctionMembers[0].users);
    const selectedUserState = useExtendedState('');
    const handleSelectedUserChange = (newValue) => {
        let parsedSelectedUser;
        try {
            parsedSelectedUser = JSON.parse(newValue);
            selectedUserState.setValue(parsedSelectedUser);
        } catch (e) {
            selectedUserState.reset();
        }
    };
    const resetMinMaxQuantities = useCallback((newMemberId) => {
        if (newMemberId) {
            const bidsForMember = bids.filter((bid) => bid.member.memberId === newMemberId);
            const numberOfFreeSlots = auction.quantity - bidsForMember.length;
            const newInitialQuantity = numberOfFreeSlots > 0 ? 1 : initialQuantity;
            minQuantityState.setValueIfNew(newInitialQuantity);
            maxQuantityState.setValueIfNew(numberOfFreeSlots);
            prices.resize(numberOfFreeSlots);
        } else {
            minQuantityState.resetIfNecessary();
            maxQuantityState.resetIfNecessary();
            prices.resize(initialMaxQuantity);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        prices.items,
        minQuantityState.resetIfNecessary,
        maxQuantityState.resetIfNecessary,
        minQuantityState.setValueIfNew,
        maxQuantityState.setValueIfNew,
        auction.quantity,
        bids,
        initialQuantity,
        initialMaxQuantity,
    ]);
    const handleSelectedMemberChange = (newMemberId) => {
        const selectedMember = auctionMembers.find(({ memberId }) => memberId === newMemberId);
        const newUsers = selectedMember ? selectedMember.users : [];
        selectedUserState.reset();
        setUsers(newUsers);
        selectedMemberIdState.setValue(newMemberId); // triggers resetMinMaxQuantities with new member id
    };
    useEffect(() => {
        resetMinMaxQuantities(selectedMemberIdState.value);
    }, [resetMinMaxQuantities, selectedMemberIdState.value]);
    const canEditAllBids = hasPrivilege(userPrivileges, [Privileges.BID_EDIT_ALL]);
    const { validators, isValid } = useMultiValidator(false, {
        members: membersValidator(t),
        users: usersValidator(t),
        price: compositeValidator(priceValidator(t, auction.minPrice, auction.maxPrice, false), wafValidator(t)),
    }, {
        members: !hasMoreMembers,
        users: !canEditAllBids,
        price: true,
    });
    const { validator: referenceValidator, isValid: isReferenceValid } = useSingleValidator(true, useMemo(() => wafValidator(t), [t]));
    const getValidPrices = (allItems) => allItems.map((item) => item.value.value).filter((price) => price !== null);
    const validPrices = getValidPrices(prices.items);
    const [isDisabled, setIsDisabled] = useState(true);
    const initialValidation = maxQuantityState.value === 0 || validPrices.length < minQuantityState.value ? Promise.reject() : Promise.resolve();
    Promise.all([initialValidation, isReferenceValid(), isValid()])
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    const successCallback = () => {
        prices.reset();
        onBidAdded();
    };
    const extendedMutation = useExtendedMutation(SUBMIT_SLOTTED_BIDS, {
        variables: {
            auctionId: auction.id,
            memberId: selectedMemberIdState.value,
            userRacf: selectedUserState.value ? selectedUserState.value.racf : currentUserRacf,
            prices: validPrices,
            asBrokerReference: asBrokerReferenceState.value,
        },
        successCallback,
        failureMessageOptions: { key: 'auctions.bids-list.place-bid.notification.place-bid-failure', params: { count: validPrices.length } },
        refetchQueries: [ALL_BIDS_QUERY_NAME],
    });
    const readonlyPriceFieldValue = selectedMemberIdState.value === ''
        ? t('auctions.bids-list.no-member-selected')
        : t('auctions.bids-list.no-free-slots');
    return (
        <form
            className="auction-bids--place-bid rich-list-row-expanded"
            onSubmit={(event) => event.preventDefault()}
        >
            <div className="auction-bids--place-bid--owner">
                {hasMoreMembers && (
                    <SelectField
                        className="auction-bids--place-bid--member"
                        label={t('auctions.bids-list.member')}
                        state={selectedMemberIdState}
                        onChange={handleSelectedMemberChange}
                        validator={validators.members}
                        emptyOption={<option value="">...</option>}
                        options={auctionMembers.map(({ memberId, auctionLimit }) => (
                            auctionLimit !== null
                                ? (<option value={memberId} key={memberId}>{memberId} (max: {auctionLimit})</option>)
                                : (<option value={memberId} key={memberId}>{memberId}</option>)
                        ))}
                    />
                )}
                {canEditAllBids && (
                    <SelectField
                        className="auction-bids--place-bid--user"
                        label={t('auctions.bids-list.user')}
                        value={selectedUserState.value === '' ? selectedUserState.value : JSON.stringify(selectedUserState.value)}
                        onChange={handleSelectedUserChange}
                        validator={validators.users}
                        emptyOption={<option value="">...</option>}
                        options={users.map((user) => <option value={JSON.stringify(user)} key={user.userCode}>{user.racf} ({user.name})</option>)}
                        isChanged={selectedUserState.isChanged}
                        onReset={selectedUserState.reset}
                    />
                )}
            </div>
            <div className="auction-bids--place-bid--prices">
                {prices.items.length > 0
                    ? prices.items.map((item) => (
                        <NumberField
                            key={`auction-bids--place-bid--price--${item.id}`}
                            inputId={`auction-bids--place-bid--price--${item.id}`}
                            className="auction-bids--place-bid--price"
                            label={t('auctions.bids-list.price')}
                            postfix={<>&nbsp;{auction.zone.currency.code}</>}
                            fractionDigits={2}
                            validator={validators.price}
                            step={priceStep}
                            min={auction.minPrice}
                            max={auction.maxPrice}
                            decrementDisabled={!isPriceStepValid}
                            incrementDisabled={!isPriceStepValid}
                            state={item.value}
                        />
                    ),
                    ) : (
                        <ReadonlyField
                            className="auction-bids--place-bid--price"
                            label={t('auctions.bids-list.price')}
                            value={readonlyPriceFieldValue}
                        />
                    )}
            </div>
            <div className="auction-bids--place-bid--button">
                {productType.is(SECURITY)
                && hasPrivilege(selectedUserState.value ? selectedUserState.value.permissions : userPrivileges, [Privileges.BID_EDIT_AS_BROKER]) && (
                    <TextField
                        className="auction-bids--place-bid--reference"
                        label={t('auctions.bids-list.reference')}
                        state={asBrokerReferenceState}
                        validator={referenceValidator}
                    />
                )}
                <BidsPlaceBidButton
                    extendedMutation={extendedMutation}
                    disabled={isDisabled}
                    label={maxQuantityState.value > 1 ? t('auctions.bids-list.button.place-bids') : t('auctions.bids-list.button.place-bid')}
                />
            </div>
            {isConflictWithBiddingUserConfigurationDefaultPrice && (
                <WarningPanel message={t('auctions.bids-list.user-configuration-conflict-with-default-price')} />
            )}
        </form>
    );
}

export default BidsPlaceBidSlotted;
